import dayjs from "dayjs";
import {
  formatToCompanyTimezone,
  formatToUtc,
  getDateTimeFromSelectedDate,
  getYearFromCompanyDate,
} from "../lib/functions";

import Big from "big.js";

// Discount for a single item row: reads whichever mode is active
// (percent, the default/undefined-safe mode, or a flat amount), clamped
// so a flat amount can never exceed the item's own line total.
const getItemDiscountAmount = (item, line) => {
  if (item?.discountMode === "amount") {
    const raw = new Big(item?.discountAmt || 0);
    return raw.gt(line) ? line : raw.lt(0) ? new Big(0) : raw;
  }
  return line.times(item?.discount || 0).div(100);
};

// src/utils/quotation.js
export const createQuotationPayload = ({
  state,
  authInfo,
  selectedDiscountAcc,
  selectedTaxAcc,
  selectedTransportAcc,
}) => {
  const {
    entryDate,
    itemCart,
    customer,
    footerValues,
    voucherNumber,
    taxMode,
    servicePayCart,
    sale_o_id,
  } = state;
  console.log("footerValues: :::: ", footerValues)

  let finalDate = entryDate || new Date();
  if (!dayjs(entryDate).isSame(dayjs(), "day")) {
    finalDate = getDateTimeFromSelectedDate(entryDate);
  }

  const formattedDate = formatToUtc(finalDate);

  let dateUTC = formatToUtc(finalDate);
  let companydate = formatToCompanyTimezone(dateUTC);
  const vchYear = getYearFromCompanyDate(companydate);

  // calculate other_service_cost
  const otherServiceCost = (state.servicePayCart || []).reduce(
    (sum, e) => sum.plus(Big(e.tran_amount || 0)),
    Big(0)
  ).toFixed(13);


  // if only one expense, map transport_acc_id, else 0
  const transportAccId =
    state.servicePayCart?.length === 1 ? state.servicePayCart[0].to_acc_id : 0;
  const isOnTotal = taxMode === "on_total";

const items = (itemCart || [])
  .filter((it) => it.item_id && !it.isBlank)
  .map((it) => {
    const isBonus = Boolean(it?.bonus);

    const qty = new Big(it.qty || 0);
    const rate = isBonus ? new Big(0) : new Big(it.rate || 0);

    const base = qty.times(rate);

    const discountAmt = isBonus ? new Big(0) : getItemDiscountAmount(it, base);
    const afterDiscount = base.minus(discountAmt);

    // Back-computed so the printed voucher (which reads this field directly
    // in "on_item" calc mode) is correct even when the row is in amount mode.
    const discountPct = isBonus
      ? new Big(0)
      : it.discountMode === "amount"
        ? (base.eq(0) ? new Big(0) : discountAmt.div(base).times(100))
        : new Big(it.discount || 0);

    const taxPer = isOnTotal || isBonus ? new Big(0) : new Big(it.tax_per || 0);
    const taxAmt = isBonus ? new Big(0) : afterDiscount.times(taxPer).div(100);

    const lineTotal = isBonus
      ? new Big(0)
      : isOnTotal
        ? base
        : afterDiscount.plus(taxAmt);

    return {
      warehouse_id: it.warehouse_id || 0,
      warehouse_name: it.warehouse_name || "",

      item_id: it.item_id,
      item_name: it.product || it.item_name,
      item_description: it.item_description || "",
      display_text: `${it.product} - ${it.item_code || ""}`,
      is_serial: "no",

      item_qty: qty.toString(),
      sale_qty: qty.times(new Big(it.conversion || 1)).toString(),
      retail_qty: 0,

      item_rate: rate.toString(),
      sale_rate: (Number(it.conversion) || 1) > 1
        ? rate.div(new Big(it.conversion || 1)).toString()
        : rate.toString(),

      item_discount: discountAmt.toFixed(13),
      item_discount_per: discountPct.toString(),
      discount_mode: it.discountMode === "amount" ? "amount" : "percent",

      item_tax: taxAmt.toFixed(13),
      item_tax_per: taxPer.toString(),

      item_total: lineTotal.toFixed(13),

      unit_id: it.unit_id || 0,
      unit_name: it.unit_name || "",
      unit_symbol: it.unit_symbol || "",

      per_unit_id: it.per_unit_id || 0,
      per_unit_symbol: it.per_unit_symbol || "",
      per_conversion: it.per_conversion || 1,
      conversion: it.conversion || 1,

      base_unit_name: it.base_unit_name || null,
      qty_display: `${it.qty || 0} ${it.unit_symbol || ""}`,

      units: it.units || [],
      serials: [],

      // ✅ bonus flag — same key pattern as sales
      bounus_update_check: isBonus,

      discount_acc_id: selectedDiscountAcc?.acc_id || 0,
      discount_acc_name: selectedDiscountAcc?.acc_name || "",

      tax_acc_id: selectedTaxAcc?.acc_id || 0,
      tax_acc_name: selectedTaxAcc?.acc_name || "",
    };
  });
  const masterData = {
    sale_o_id: sale_o_id || 0,
    sale_order_no: voucherNumber,
    narration: footerValues?.description || "",
    terms_conditions: footerValues?.termsConditions || "",
    payment_terms: footerValues?.paymentTerms || "",
    show_terms_conditions: footerValues?.showTermsAndConditions ? "yes" : "no",
    created_date: formattedDate,
    is_cal_type: taxMode === "on_item" ? "individual" : "on_total",
    is_serial: "no",
    // transport_acc_id: authInfo?.default_transport_acc_id || 0,
    sub_total: state.footerValues.subtotal,
    total_discount_per: state.footerValues.discountPct,
    total_discount: state.footerValues.total_discount,
    total_tax_per: state.footerValues.taxPct,
    total_tax: state.footerValues.totalTax,
    total_transport_cost: state.footerValues.serviceCharge || 0,
    other_service_cost: otherServiceCost, // ✅ old UI
    transport_acc_id: selectedTransportAcc.acc_id, // ✅ old UI
    discount_acc_id: selectedDiscountAcc.acc_id,
    tax_acc_id: selectedTaxAcc.acc_id,
    total_amount: state.footerValues.grandTotal,
    conversion_rate: 0,
    conversion_currency: null,
    ...(state.tagFieldId && state.tagFieldId.length > 0
      ? { tag_field_id: state.tagFieldId }
      : {}),
  };

  return {
    sale_o_id: sale_o_id || 0,
    vchdate: vchYear,
    customer: {
      acc_id: customer?.acc_id,
      acc_name: customer?.acc_name,
      institution_name: customer?.institution_name,
      contact_no: customer?.contact_no,
      address: customer?.address,
      country_code: customer?.country_code,
    },
    itemCart: items,
    masterData,
    sub_total: footerValues?.subtotal || 0,
    total_amount: footerValues?.grandTotal || 0,
    total_discount: footerValues?.total_discount || 0,
    total_discount_per: footerValues?.discountPct || 0,
    total_tax: footerValues?.totalTax || 0,
    total_tax_per: footerValues?.taxPct || 0,
    total_transport_cost: footerValues?.serviceCharge || 0,
    other_service_cost: otherServiceCost, // ✅ old UI
    transport_acc_id: selectedTransportAcc.acc_id, // ✅ old UI
    conversion_currency: null,
    conversion_rate: 0,
    payServiceCart: servicePayCart || [],
  };
};
